<template>
    <div>
        <page-title title="Batches" icon="pe-7s-box2"/>

        <div v-if="!isLoading">
            <b-row class="mb-4">
                <b-col lg="12" class="d-flex d-flex col-lg-12 justify-content-end">
                    <b-button v-if="hasPermission('orders.batch.create')"
                              :to="shipmentBatchCreateRouterParams"
                              class="bg-midnight-bloom text-white">Nieuwe batch</b-button>
                </b-col>
            </b-row>
            <b-row class="mb-4">
                <b-col lg="12">
                    <b-card>
                        <b-card-title>
                          FILTERS
                        </b-card-title>

                        <b-form>
                            <b-row>
                                <b-col>
                                    <label>{{ $t(orderListColumnTitles.dispatcher_type) }}</label><br/>
                                    <select v-model="filterForm.dispatcher_type" class="form-control form-control-sm" :class="{isFilled: filterForm.dispatcher_type !== ''}"
                                            @change="filterDispatcherTypeChanged" :disabled="!isFieldEditable('dispatcher_type')">
                                        <option v-for="(display_name, identifier) in relevantDispatcherTypes" :value="identifier" :key="identifier">{{ display_name }}</option>
                                    </select>
                                </b-col>
                                <b-col>
                                    <label>{{ $t(orderListColumnTitles.dispatcher_identifier) }}</label><br/>
                                    <select v-model="filterForm.dispatcher_identifier" class="form-control form-control-sm" :class="{isFilled: filterForm.dispatcher_identifier !== ''}"
                                            :disabled="!isFieldEditable('dispatcher_identifier')">
                                        <option v-for="(dispatcher, index) in dispatchers" :value="dispatcher.identifier" :key="index">{{ dispatcher.display_name }}</option>
                                    </select>
                                </b-col>
                                <b-col>
                                    <label>{{ $t(orderListColumnTitles.dispatch_request_batch_type) }}</label><br/>
                                    <select v-model="filterForm.batch_type" class="form-control form-control-sm" :class="{isFilled: filterForm.batch_type !== ''}">
                                        <option value=""></option>
                                        <option v-for="(batchType, index) in batchTypesSorted" :value="batchType.identifier" :key="index">{{ batchType.display_name }}</option>
                                    </select>
                                </b-col>
                                <b-col>
                                    <label>{{ $t(orderListColumnTitles.dispatch_request_batch_status) }}</label><br/>
                                    <select v-model="filterForm.status" class="form-control form-control-sm" :class="{isFilled: filterForm.status !== ''}">
                                        <option value=""></option>
                                        <option v-for="(display_name, identifier) in dispatchRequestBatchStatuses" :value="identifier" :key="identifier">{{ display_name }}</option>
                                    </select>
                                </b-col>
                            </b-row>
                        </b-form>
                    </b-card>
                </b-col>
            </b-row>

            <b-card>
                <b-card-title>RESULTATEN</b-card-title>

                <template v-if="isSearching">
                  <div class="loading">
                    <span class="text">
                      Gegevens ophalen&hellip;
                    </span>
                    <span class="icon">
                      <font-awesome-icon icon="spinner" spin/>
                    </span>
                  </div>
                </template>

                <template v-else-if="batches.length === 0">
                  <b-alert show variant="light">
                    Geen batches gevonden.
                  </b-alert>
                </template>

                <template v-else>
                  <!-- DESKTOP -->
                  <div class="d-none d-md-block">

                    <b-table :items="batches" :fields="fields.default">
                      <template #cell(created_at)="row">
                        {{ row.value | datetimeshort }}
                      </template>

                      <template #cell(dispatcher)="row">
                        <location-link :location="row.value"/>
                      </template>

                      <template #cell(type)="row">
                        {{ row.value.display_name }}
                      </template>

                      <template #cell(identifier)="row">
                        <router-link :to="getBatchRoute(row.item)">
                          {{ row.value }}
                        </router-link>
                      </template>

                      <template #cell(dispatch_request_count)="row">
                        {{ row.value }}
                      </template>

                      <template #cell(status)="row">
                        {{ row.value | dispatchRequestBatchStatus }}
                      </template>

                      <template #cell(storage_zone)="row">
                        {{ row.value ? row.value.display_name : ''}}
                      </template>
                    </b-table>
                  </div>
                  <!-- END DESKTOP -->

                  <!-- MOBILE -->
                  <div class="d-md-none">
                    <b-table :items="batches" :fields="fields.mobile" stacked>
                      <template #cell(identifier)="row">
                        <router-link :to="getBatchRoute(row.item)">
                          {{ row.value }}
                        </router-link>
                      </template>

                      <template #cell(status)="row">
                        {{ row.value | dispatchRequestBatchStatus }}
                      </template>
                    </b-table>
                  </div>
                  <!-- END MOBILE -->
                </template>
            </b-card>
        </div>
    </div>
</template>

<script>
import PageTitle from "../../components/PageTitle.vue";
import orderListColumnTitles from "../../data/orderListColumnTitles";
import LocationLink from "../../components/LocationLink.vue";
import _ from "lodash"

const RELEVANT_DISPATCHER_TYPES = ['dc', 'store']
const LOCALSTORAGE_KEY_FILTERS = 'batches.filters'

export default {
    name: "ShipmentBatchesIndex",
    components: {LocationLink, PageTitle},

    data() {
        return {
            isLoading: true,
            isSearching: false,
            batches: [],
            orderListColumnTitles, // for use in template
            fields: {
                'default': [
                  { key: 'identifier',             label: 'Batchnummer' },
                  { key: 'created_at',             label: 'Aangemaakt op' },
                  { key: 'status',                 label: this.$t(orderListColumnTitles.dispatch_request_batch_status) },
                  { key: 'type',                   label: 'Type' },
                  { key: 'dispatch_request_count', label: 'Aantal orders' },
                  { key: 'dispatcher',             label: 'Locatie' },
                  { key: 'storage_zone',           label: 'Sorteerzone' },
                ],
                'mobile': [
                  { key: 'identifier',             label: 'Batchnummer' },
                  { key: 'status',                 label: this.$t(orderListColumnTitles.dispatch_request_batch_status) },
                ]
            },
            filterForm: {
                dispatcher_type: '',
                dispatcher_identifier: '',
                batch_type: '',
                status: '',
            },
        }
    },

    computed: {
        dispatchers() {
            return this.$store.state.locationsForUser
                .filter(d => {
                    return d.location_type.identifier === this.filterForm.dispatcher_type
                        && d.location_properties.canProcessProducts
                })
                .sort((a, b) => {
                    if (a.display_name > b.display_name) return 1
                    if (a.display_name < b.display_name) return -1
                    return 0
                })
        },
        relevantDispatcherTypes() {
            let relevantDispatchers = {}
            RELEVANT_DISPATCHER_TYPES.forEach(type => {
                relevantDispatchers[type] = this.dispatcherTypes[type]
            })
            return relevantDispatchers
        },
        batchTypesSorted() {
            return _.orderBy(this.$store.state.dispatchRequestBatchTypes, 'display_name')
        },
        shipmentBatchCreateRouterParams() {
            return {
                name:'ShipmentBatchCreate',
                params: {
                    locationTypeIdentifier: this.filterForm.dispatcher_type,
                    locationIdentifier: this.filterForm.dispatcher_identifier,
                },
            };
        },
    },

    mounted() {
        Promise.all([
            this.$store.getters.getLocationDataTypes,
            this.$store.getters.getLocationsForUser,
            this.$store.getters.getDispatchRequestBatchTypes,
        ])
            .then(() => {
                const cachedFilters = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY_FILTERS)) ?? {}
                if (!_.difference(Object.keys(this.filterForm), Object.keys(cachedFilters)).length) {
                    this.$set(this, 'filterForm', cachedFilters)
                }
                else {
                    this.filterForm.dispatcher_type = this.$store.state.locationsForUser[0]?.location_type?.identifier
                    this.filterForm.dispatcher_identifier = this.$store.state.locationsForUser[0]?.identifier
                }
                this.isLoading = false
                // this.searchBatches()
            })
    },

    methods: {
        filterDispatcherTypeChanged() {
            this.filterForm.dispatcher_identifier = ''
        },
        isFieldEditable(field) {
            switch (field) {
                case 'dispatcher_type':
                case 'dispatcher_identifier':
                    return this.$store.state.locationsForUser.length > 1
                default:
                    return true
            }
        },
        searchBatches() {
            if (!this.filterForm.dispatcher_type || !this.filterForm.dispatcher_identifier) {
                return
            }

            this.isSearching = true

            let optionalParams = {
                status: [],
            }
            if (this.filterForm.status) {
                optionalParams.status.push(this.filterForm.status)
            }
            if (this.filterForm.batch_type) {
                optionalParams.batch_type = this.filterForm.batch_type
            }

            const notStatuses = _.difference(['closed'], optionalParams.status)

            this.batches = []
            this.$http.get(`/api/rest/locations/${this.filterForm.dispatcher_type}/${this.filterForm.dispatcher_identifier}/dispatch-request-batches`, {
                params: {
                    ...optionalParams,
                    not_status: notStatuses,
                    sort_by: 'created_at',
                    sort_dir: 'asc',
                    include: [
                        'dispatcher',
                        'storage_zone',
                        'type',
                    ].join(','),
                }
            })
                .then(response => {
                    this.batches = response.data
                })
                .catch(e => {
                    this.$alert(`Fout bij het ophalen van batches.`, 'danger');
                    console.error(`Fout bij het ophalen van batches: ${e}`);
                })
                .then(() => this.isSearching = false)
        },
        getBatchRoute(batch) {
            return {
                name: 'ShipmentBatchView',
                params: {
                    locationTypeIdentifier: batch.dispatcher_type,
                    locationIdentifier: batch.dispatcher_identifier,
                    batchIdentifier: batch.identifier,
                },
            }
        },
    },

    watch: {
        filterForm: {
            deep: true,
            handler(newVal) {
                if (!this.dispatchers.some(d => d.identifier === newVal.dispatcher_identifier)) {
                    this.filterForm.dispatcher_type = newVal.dispatcher_type = this.$store.state.locationsForUser[0]?.location_type?.identifier
                    this.filterForm.dispatcher_identifier = newVal.dispatcher_identifier = this.$store.state.locationsForUser[0]?.identifier
                }

                this.searchBatches()
                localStorage.setItem(LOCALSTORAGE_KEY_FILTERS, JSON.stringify(newVal))
            }
        }
    },
}
</script>

<style scoped lang="scss">
input[disabled],
select[disabled] {
    background: transparent;
    border-style: none;

    -moz-appearance: none;
    -webkit-appearance: none;

    &::-ms-expand {
        display: none;
    }
}
</style>
